/**
 * Which canvas a case opens on, decided BEFORE any canvas mounts.
 *
 * Order: an explicit `?view=` > the view this browser tab last used on this
 * case (sessionStorage) > the officer's last choice on any case (localStorage)
 * > DEFAULT_CANVAS_VIEW. Nothing here reads the graph, so the first paint is the
 * final view: no canvas is mounted and thrown away.
 *
 * The node-count rule (`defaultCanvasView`) no longer switches anything. It only
 * says whether a summary is worth suggesting; the page shows that as a
 * dismissible hint (`summaryHintDismissed`).
 */

/** The owner's one-line switch: set to 'consolidated' to open on the summary by default. */
export const DEFAULT_CANVAS_VIEW: CanvasViewChoice = 'evidence';

export const CONSOLIDATED_DEFAULT_ABOVE_NODES = 60;

export type CanvasViewChoice = 'evidence' | 'consolidated';

/** Toggle labels: the officer should know a summary exists. */
export const VIEW_LABEL: Record<CanvasViewChoice, string> = {
  evidence: 'Full evidence',
  consolidated: 'Summary view',
};

const CASE_VIEW_KEY = (caseId: string) => `fusion.canvasView.${caseId}`;
const USER_VIEW_KEY = 'fusion.canvasView.default';
const HINT_KEY = (caseId: string) => `fusion.summaryHint.dismissed.${caseId}`;

const isView = (v: unknown): v is CanvasViewChoice => v === 'evidence' || v === 'consolidated';

function read(store: 'session' | 'local', key: string): string | null {
  try {
    return (store === 'session' ? sessionStorage : localStorage).getItem(key);
  } catch {
    return null;
  }
}
function write(store: 'session' | 'local', key: string, value: string): void {
  try {
    (store === 'session' ? sessionStorage : localStorage).setItem(key, value);
  } catch { /* storage unavailable: the choice lasts for this page only */ }
}

export function readRememberedView(caseId: string | undefined): CanvasViewChoice | null {
  if (caseId) {
    const perCase = read('session', CASE_VIEW_KEY(caseId));
    if (isView(perCase)) return perCase;
  }
  const perUser = read('local', USER_VIEW_KEY);
  return isView(perUser) ? perUser : null;
}

export function rememberView(caseId: string | undefined, view: CanvasViewChoice): void {
  if (caseId) write('session', CASE_VIEW_KEY(caseId), view);
  write('local', USER_VIEW_KEY, view);
}

export function initialCanvasView(input: { viewParam: string | null; caseId: string | undefined }): CanvasViewChoice {
  if (isView(input.viewParam)) return input.viewParam;
  return readRememberedView(input.caseId) ?? DEFAULT_CANVAS_VIEW;
}

export function summaryHintDismissed(caseId: string | undefined): boolean {
  return !!caseId && read('session', HINT_KEY(caseId)) === '1';
}

export function dismissSummaryHint(caseId: string | undefined): void {
  if (caseId) write('session', HINT_KEY(caseId), '1');
}

/**
 * Suggestion rule only (never a switch): a case-worker case with more than
 * about 60 nodes has a summary worth pointing at.
 */
export function defaultCanvasView(input: {
  viewParam: string | null;
  nodeCount: number;
  hasCaseWorkerRun: boolean;
}): CanvasViewChoice {
  if (isView(input.viewParam)) return input.viewParam;
  if (input.hasCaseWorkerRun && input.nodeCount > CONSOLIDATED_DEFAULT_ABOVE_NODES) {
    return 'consolidated';
  }
  return 'evidence';
}
